import React, { useState, useRef, useEffect } from 'react';
import Navbar from '../components/studentnavbar';
import Sidebar from "../components/studentsidebar";
import { useSidebar } from '../components/SidebarContext';
import axios from 'axios';

const StudentAccount = () => {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const [profileImage, setProfileImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    admissionNo: '',
    collegeName: '',
    email: '',
    username: '',
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    address: '',
    phone: '',
  });
  const fileInputRef = useRef(null);
  const tempImageUrlRef = useRef(null);

  // Phone number validation function
  const validatePhoneNumber = (phone) => {
    // Remove any non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  // Fetch profile data
  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/MyAccountRoutes/profile', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      
      const profileData = response.data;
      
      setFormData({
        fullName: profileData.fullName || '',
        admissionNo: profileData.admissionNo || '',
        collegeName: profileData.collegeName || '',
        email: profileData.email || '',
        username: profileData.username || '',
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        address: profileData.address || '',
        phone: profileData.phone || '',
      });

      if (profileData.profilePhoto || profileData.profilePhotoUrl) {
        const photoUrl = profileData.profilePhotoUrl || 
          `${window.location.origin}/api/MyAccountRoutes${profileData.profilePhoto}`;
        
        const img = new Image();
        img.onload = () => setProfileImage(photoUrl);
        img.onerror = () => {
          console.warn('Profile photo not accessible:', photoUrl);
          setProfileImage(null);
        };
        img.src = photoUrl;
      } else {
        setProfileImage(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileImage(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    return () => {
      if (tempImageUrlRef.current) {
        URL.revokeObjectURL(tempImageUrlRef.current);
        tempImageUrlRef.current = null;
      }
    };
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPEG, JPG, PNG, and GIF files are allowed');
        return;
      }
      if (tempImageUrlRef.current) {
        URL.revokeObjectURL(tempImageUrlRef.current);
      }
      const imageUrl = URL.createObjectURL(file);
      tempImageUrlRef.current = imageUrl;
      setProfileImage(imageUrl);
      setFormData({ ...formData, profilePhoto: file });
    }
  };

  const handleEditIconClick = () => {
    fileInputRef.current.click();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Allow only digits and limit to 10 characters
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData({ ...formData, [name]: digitsOnly });
      }
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Phone number validation
    if (formData.phone.trim() && !validatePhoneNumber(formData.phone)) {
      alert('Phone number must be exactly 10 digits');
      return;
    }
    
    // Password validation
    if (formData.oldPassword || formData.newPassword || formData.confirmNewPassword) {
      if (!formData.oldPassword) {
        alert('Please enter your old password');
        return;
      }
      if (!formData.newPassword) {
        alert('Please enter a new password');
        return;
      }
      if (formData.newPassword.length < 6) {
        alert('New password must be at least 6 characters long');
        return;
      }
      if (formData.newPassword !== formData.confirmNewPassword) {
        alert('New password and confirm password do not match');
        return;
      }
      if (formData.oldPassword === formData.newPassword) {
        alert('New password must be different');
        return;
      }
    }

    const form = new FormData();
    if (formData.address.trim()) form.append('address', formData.address.trim());
    if (formData.phone.trim()) form.append('phone', formData.phone.trim());
    if (formData.profilePhoto) form.append('profilePhoto', formData.profilePhoto);
    if (formData.oldPassword) form.append('oldPassword', formData.oldPassword);
    if (formData.newPassword) form.append('newPassword', formData.newPassword);
    if (formData.confirmNewPassword) form.append('confirmNewPassword', formData.confirmNewPassword);

    try {
      await axios.post('/api/MyAccountRoutes/profile', form, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      alert('Profile updated successfully');
      setFormData(prev => ({
        ...prev,
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        profilePhoto: null
      }));
      if (tempImageUrlRef.current) {
        URL.revokeObjectURL(tempImageUrlRef.current);
        tempImageUrlRef.current = null;
      }
      await fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      alert(errorMessage);
    }
  };

  const styles = {
    layout: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: '100vh',
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
    },
    sidebar: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      width: sidebarWidth,
      zIndex: 10,
      transition: 'width 0.3s ease',
      borderRadius: '30px',
      flexShrink: 0,
      fontSize: '16px',
    },
    mainContent: {
      marginLeft: sidebarWidth,
      flex: 1,
      transition: 'margin-left 0.3s ease',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    formWrapper: {
      flex: 1,
      margin: '6rem 2rem 2rem 2rem',
      padding: '15px',
      backgroundColor: 'white',
      overflowY: 'auto',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
      position: 'relative',
    },
    sectionsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '-3rem',
      width: '100%',
      maxWidth: '900px',
      alignSelf: 'center',
      marginBottom: '5rem',
    },
    profilePhotoWrapper: {
      position: 'relative',
      width: '100px',
      height: '100px',
      flexShrink: 0,
      marginRight: '1rem',
    },
    profilePhoto: {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      backgroundColor: '#F3F4F6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      color: '#6B7280',
      border: '3px solid #E5E7EB',
      objectFit: 'cover',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    profilePhotoImg: {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      objectFit: 'cover',
    },
    loadingSpinner: {
      fontSize: '12px',
      color: '#6B7280',
      fontFamily: "'Roboto', sans-serif",
    },
    editIcon: {
      position: 'absolute',
      bottom: '1.5rem',
      right: '0',
      transform: 'rotate(180deg)',
      width: '24px',
      height: '24px',
      backgroundColor: '#8B5CF6',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease, transform 0.3s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      fontFamily: "'Roboto', sans-serif",
    },
    fieldRow: {
      display: 'flex',
      flexDirection: 'row',
      gap: '1rem',
      width: '100%',
      padding: '1rem',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    formColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: '200px',
      marginTop: 1,
    },
    fullNameColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: '200px',
      maxWidth: '300px',
      marginTop: 1,
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontSize: '14px',
      color: '#6B21A8',
      fontWeight: '500',
      fontFamily: "'Roboto', sans-serif",
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      fontSize: '14px',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      backgroundColor: '#FFFFFF',
      transition: 'border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease',
      boxSizing: 'border-box',
      color: '#1F2937',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontFamily: "'Roboto', sans-serif",
    },
    disabledInput: {
      width: '100%',
      padding: '8px 12px',
      fontSize: '14px',
      border: '1px solid #D1D5DB',
      borderRadius: '6px',
      backgroundColor: '#F5F5F5',
      color: '#6B7280',
      boxSizing: 'border-box',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      cursor: 'not-allowed',
      fontFamily: "'Roboto', sans-serif",
    },
    bottomRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '1rem',
      width: '100%',
      maxWidth: '900px',
      position: 'absolute',
      bottom: '2.5rem',
      padding: '0 1rem',
      justifyContent: 'flex-end',
    },
    saveButton: {
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  return (
    <div style={styles.layout} className="student-account-wrapper">
      <style>
        {`
          /* Optional: Define @font-face if Roboto is not available locally */
          /*
          @font-face {
            font-family: 'Roboto';
            src: url('/path/to/Roboto-Regular.woff2') format('woff2'),
                 url('/path/to/Roboto-Regular.woff') format('woff');
            font-weight: 400;
            font-style: normal;
          }
          @font-face {
            font-family: 'Roboto';
            src: url('/path/to/Roboto-Medium.woff2') format('woff2'),
                 url('/path/to/Roboto-Medium.woff') format('woff');
            font-weight: 500;
            font-style: normal;
          }
          @font-face {
            font-family: 'Roboto';
            src: url('/path/to/Roboto-Bold.woff2') format('woff2'),
                 url('/path/to/Roboto-Bold.woff') format('woff');
            font-weight: 700;
            font-style: normal;
          }
          */

          /* Apply Roboto to all elements within the wrapper */
          .student-account-wrapper, .student-account-wrapper * {
            font-family: 'Roboto', sans-serif !important;
          }

          /* Scope styles to StudentAccount wrapper */
          .student-account-wrapper .input-field:focus {
            border-color: #6B21A8 !important;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
         
          }

          .student-account-wrapper .glow-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }

          .student-account-wrapper .edit-icon:hover {
            background-color: #A78BFA !important;
            transform: rotate(360deg);
          }

          /* Normalize Sidebar font size */
          .sidebar-container, .sidebar-container * {
            font-size: 16px !important;
          }
        `}
      </style>

      <div style={styles.sidebar} className="sidebar-container">
        <Sidebar />
      </div>

      <div style={styles.mainContent}>
        <Navbar />
        <div style={styles.formWrapper}>
          <div style={styles.sectionsContainer}>
            <div style={styles.fieldRow}>
              <div style={styles.profilePhotoWrapper}>
                <div style={styles.profilePhoto}>
                  {isLoading ? (
                    <div style={styles.loadingSpinner}>Loading...</div>
                  ) : profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      style={styles.profilePhotoImg}
                      onError={() => {
                        console.warn('Failed to load profile image');
                        setProfileImage(null);
                      }}
                    />
                  ) : (
                    'No Photo'
                  )}
                </div>
                <div
                  style={styles.editIcon}
                  className="edit-icon"
                  onClick={handleEditIconClick}
                  title="Upload profile photo"
                >
                  ✎
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
              </div>
              <div style={styles.fullNameColumn}>
                <label style={styles.label}>Full name</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
              </div>
            </div>
            <div style={styles.fieldRow}>
              <div style={styles.formColumn}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>Phone number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                  placeholder="Enter 10 digit phone number"
                  maxLength="10"
                />
              </div>
            </div>
            <div style={styles.fieldRow}>
              <div style={styles.formColumn}>
                <label style={styles.label}>College</label>
                <input
                  type="text"
                  name="collegeName"
                  value={formData.collegeName}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>College admission no</label>
                <input
                  type="text"
                  name="admissionNo"
                  value={formData.admissionNo}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                  placeholder="Enter your address"
                />
              </div>
            </div>
            <div style={styles.fieldRow}>
              <div style={styles.formColumn}>
                <label style={styles.label}>Old password</label>
                <input
                  type="password"
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>New password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>Confirm new password</label>
                <input
                  type="password"
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                />
              </div>
            </div>
            </div>
            <div style={styles.bottomRow}>
              <button
                style={styles.saveButton}
                className="glow-button"
                onClick={handleSubmit}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default StudentAccount;